// Main application JavaScript
import { 
    currentLanguage, 
    setCurrentLanguage, 
    t, 
    getCurrencyName, 
    updateTranslations, 
    updateThemeButtonText 
} from './translations.js';

class CurrencyConverter {
    constructor() {
        this.currencies = {};
        this.popularCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'AUD', 'CAD'];
        this.watchList = this.loadWatchList();
        this.history = this.loadHistory(); // Load history from local storage
        this.init();
    }

    async init() {
        await this.loadCurrencies();
        this.setupEventListeners();
        this.setupTheme();
        this.setupLanguage();
        this.loadPopularRates();
        this.loadWatchListRates();
        this.renderHistory(); // Render history on initialization
        
        // Initial conversion
        this.convertCurrency();
    }

    async loadCurrencies() {
        try {
            // Direct API call to external currency API
            const response = await this.fetchWithFallback('/currencies.json');
            this.currencies = response;
            this.populateCurrencySelects();
        } catch (error) {
            console.error('Error loading currencies:', error);
            this.showError('Failed to load currencies. Please refresh the page.');
        }
    }

    // Helper function to fetch with fallback (same as server.js logic)
    async fetchWithFallback(endpoint) {
        const CURRENCY_API_BASE = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1';
        const FALLBACK_API_BASE = 'https://latest.currency-api.pages.dev/v1';
        
        try {
            const response = await fetch(`${CURRENCY_API_BASE}${endpoint}`);
            if (!response.ok) throw new Error('Primary API failed');
            return await response.json();
        } catch (error) {
            console.log('Primary API failed, trying fallback...');
            try {
                const response = await fetch(`${FALLBACK_API_BASE}${endpoint}`);
                if (!response.ok) throw new Error('Fallback API failed');
                return await response.json();
            } catch (fallbackError) {
                throw new Error('Both primary and fallback APIs failed');
            }
        }
    }

    populateCurrencySelects() {
        const fromSelect = document.getElementById('fromCurrency');
        const toSelect = document.getElementById('toCurrency');
        
        // Clear existing options
        fromSelect.innerHTML = '';
        toSelect.innerHTML = '';
        
        // Sort currencies alphabetically
        const sortedCurrencies = Object.entries(this.currencies).sort((a, b) => {
            return a[0].localeCompare(b[0]);
        });
        
        // Populate selects
        sortedCurrencies.forEach(([code, name]) => {
            if (name) { // Skip empty names
                const optionText = getCurrencyName(code, name);
                
                const fromOption = new Option(optionText, code);
                const toOption = new Option(optionText, code);
                
                fromSelect.appendChild(fromOption);
                toSelect.appendChild(toOption);
            }
        });
        
        // Set default values
        fromSelect.value = 'usd';
        toSelect.value = 'eur';
    }

    setupEventListeners() {
        // Form submission
        document.getElementById('converterForm').addEventListener('submit', (e) => {
            e.preventDefault();
            this.convertCurrency();
        });

        // Real-time conversion on input change
        document.getElementById('amount').addEventListener('input', () => {
            this.debounce(() => this.convertCurrency(), 500)();
        });

        // Currency change
        document.getElementById('fromCurrency').addEventListener('change', () => {
            this.convertCurrency();
            this.loadPopularRates();
        });
        
        document.getElementById('toCurrency').addEventListener('change', () => {
            this.convertCurrency();
        });

        // Swap currencies
        document.getElementById('swapCurrencies').addEventListener('click', () => {
            this.swapCurrencies();
        });

        // Theme toggle
        document.getElementById('themeToggle').addEventListener('click', () => {
            this.toggleTheme();
        });

        // Language toggle
        document.getElementById('languageToggle').addEventListener('click', () => {
            this.toggleLanguage();
        });

        // Add to watch list
        document.getElementById('addToWatchBtn').addEventListener('click', () => {
            this.addToWatchList();
        });

        // Clear all watch list items
        document.getElementById('clearWatchListBtn').addEventListener('click', () => {
            this.clearAllWatchList();
        });

        // Navbar burger menu
        const navbarBurger = document.querySelector('.navbar-burger');
        const navbarMenu = document.querySelector('.navbar-menu');
        
        if (navbarBurger) {
            navbarBurger.addEventListener('click', () => {
                navbarBurger.classList.toggle('is-active');
                navbarMenu.classList.toggle('is-active');
            });
        }
        
        // Clear history
        document.getElementById('clearHistoryBtn').addEventListener('click', () => {
            this.clearHistory();
        });
    }

    setupTheme() {
        const savedTheme = localStorage.getItem('theme') || 'light';
        document.documentElement.setAttribute('data-theme', savedTheme);
        this.updateThemeIcon(savedTheme);
    }

    setupLanguage() {
        const savedLanguage = localStorage.getItem('language') || 'en';
        setCurrentLanguage(savedLanguage);
        updateTranslations();
        
        // Update language button text
        const languageButton = document.getElementById('languageToggle');
        const languageSpan = languageButton.querySelector('span:last-child');
        languageSpan.textContent = currentLanguage === 'en' ? t('chinese') : t('english');
    }

    toggleTheme() {
        const currentTheme = document.documentElement.getAttribute('data-theme');
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
        
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
        this.updateThemeIcon(newTheme);
        updateThemeButtonText();
    }

    updateThemeIcon(theme) {
        const themeIcon = document.querySelector('#themeToggle .icon i');
        themeIcon.className = theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon';
    }

    toggleLanguage() {
        const newLanguage = currentLanguage === 'en' ? 'zh' : 'en';
        setCurrentLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);
        updateTranslations();
        
        // Update language button text
        const languageButton = document.getElementById('languageToggle');
        const languageSpan = languageButton.querySelector('span:last-child');
        languageSpan.textContent = newLanguage === 'en' ? t('chinese') : t('english');
        
        // Repopulate currency selects with new language
        this.populateCurrencySelects();
        
        // Reload popular rates with new language
        this.loadPopularRates();
    }

    swapCurrencies() {
        const fromSelect = document.getElementById('fromCurrency');
        const toSelect = document.getElementById('toCurrency');
        
        const fromValue = fromSelect.value;
        const toValue = toSelect.value;
        
        fromSelect.value = toValue;
        toSelect.value = fromValue;
        
        this.convertCurrency();
        this.loadPopularRates();
    }

    async convertCurrency() {
        const amount = parseFloat(document.getElementById('amount').value);
        const from = document.getElementById('fromCurrency').value;
        const to = document.getElementById('toCurrency').value;

        if (!amount || amount <= 0) {
            this.hideResult();
            return;
        }

        this.showLoading();

        try {
            // Direct API call to get exchange rates
            const data = await this.fetchWithFallback(`/currencies/${from.toLowerCase()}.json`);
            
            if (!data[from.toLowerCase()] || !data[from.toLowerCase()][to.toLowerCase()]) {
                throw new Error('Invalid currency pair');
            }

            const rate = data[from.toLowerCase()][to.toLowerCase()];
            const convertedAmount = amount * rate;
            
            const result = {
                from: from.toUpperCase(),
                to: to.toUpperCase(),
                amount: amount,
                rate,
                convertedAmount,
                timestamp: new Date().toISOString()
            };

            this.showResult(result);
            this.addHistory(from, to, amount, result.convertedAmount); // Add to history
        } catch (error) {
            console.error('Conversion error:', error);
            this.showError(t('error-message'));
        } finally {
            this.hideLoading();
        }
    }

    async loadPopularRates() {
        const baseCurrency = document.getElementById('fromCurrency').value;
        const popularRatesContainer = document.getElementById('popularRates');
        
        try {
            // Direct API call to get exchange rates
            const data = await this.fetchWithFallback(`/currencies/${baseCurrency.toLowerCase()}.json`);
            const rates = data[baseCurrency.toLowerCase()];
            
            popularRatesContainer.innerHTML = '';
            
            this.popularCurrencies.forEach(currency => {
                if (currency.toLowerCase() !== baseCurrency && rates[currency.toLowerCase()]) {
                    const rate = rates[currency.toLowerCase()];
                    const currencyName = getCurrencyName(currency, this.currencies[currency.toLowerCase()] || currency);
                    
                    const rateElement = document.createElement('div');
                    rateElement.className = 'column is-6-tablet is-3-desktop';
                    rateElement.innerHTML = `
                        <div class="popular-rate-item">
                            <div class="popular-rate-currency">${currency}</div>
                            <div class="popular-rate-value">${this.formatNumber(rate)}</div>
                        </div>
                    `;
                    
                    popularRatesContainer.appendChild(rateElement);
                }
            });
        } catch (error) {
            console.error('Error loading popular rates:', error);
        }
    }

    showLoading() {
        document.getElementById('loadingIndicator').classList.remove('is-hidden');
        document.getElementById('resultContainer').classList.add('is-hidden');
        document.getElementById('errorContainer').classList.add('is-hidden');
    }

    hideLoading() {
        document.getElementById('loadingIndicator').classList.add('is-hidden');
    }

    showResult(result) {
        const resultAmount = document.getElementById('resultAmount');
        const resultCurrency = document.getElementById('resultCurrency');
        const exchangeRate = document.getElementById('exchangeRate');
        const lastUpdated = document.getElementById('lastUpdated');
        
        resultAmount.textContent = this.formatNumber(result.convertedAmount);
        resultCurrency.textContent = result.to;
        exchangeRate.textContent = `1 ${result.from} = ${this.formatNumber(result.rate)} ${result.to}`;
        lastUpdated.textContent = `${t('last-updated')} ${new Date().toLocaleString(currentLanguage === 'zh' ? 'zh-CN' : 'en-US')}`;
        
        document.getElementById('resultContainer').classList.remove('is-hidden');
        document.getElementById('errorContainer').classList.add('is-hidden');
    }

    showError(message) {
        document.getElementById('errorMessage').textContent = message;
        document.getElementById('errorContainer').classList.remove('is-hidden');
        document.getElementById('resultContainer').classList.add('is-hidden');
    }

    hideResult() {
        document.getElementById('resultContainer').classList.add('is-hidden');
        document.getElementById('errorContainer').classList.add('is-hidden');
    }

    formatNumber(num) {
        return new Intl.NumberFormat(currentLanguage === 'zh' ? 'zh-CN' : 'en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6
        }).format(num);
    }

    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // Watch List Methods
    loadWatchList() {
        const saved = localStorage.getItem('currencyWatchList');
        return saved ? JSON.parse(saved) : [];
    }

    saveWatchList() {
        localStorage.setItem('currencyWatchList', JSON.stringify(this.watchList));
    }

    addToWatchList() {
        const fromCurrency = document.getElementById('fromCurrency').value.toUpperCase();
        const toCurrency = document.getElementById('toCurrency').value.toUpperCase();
        
        // Create a currency pair
        const pair = `${fromCurrency}/${toCurrency}`;
        
        // Check if already in watch list
        if (!this.watchList.some(item => item.pair === pair)) {
            this.watchList.push({
                pair: pair,
                from: fromCurrency,
                to: toCurrency,
                addedAt: new Date().toISOString()
            });
            
            this.saveWatchList();
            this.loadWatchListRates();
        }
    }

    removeFromWatchList(pair) {
        this.watchList = this.watchList.filter(item => item.pair !== pair);
        this.saveWatchList();
        this.loadWatchListRates();
    }

    clearAllWatchList() {
        if (confirm(t('confirm-clear-watch-list'))) {
            this.watchList = [];
            this.saveWatchList();
            this.loadWatchListRates();
        }
    }

    async loadWatchListRates() {
        const watchListContainer = document.getElementById('watchListRates');
        const emptyMessage = document.getElementById('watchListEmpty');
        
        if (this.watchList.length === 0) {
            emptyMessage.classList.remove('is-hidden');
            watchListContainer.classList.add('is-hidden');
            return;
        }
        
        emptyMessage.classList.add('is-hidden');
        watchListContainer.classList.remove('is-hidden');
        watchListContainer.innerHTML = '';
        
        for (const item of this.watchList) {
            try {
                // Direct API call to get exchange rates
                const data = await this.fetchWithFallback(`/currencies/${item.from.toLowerCase()}.json`);
                const rates = data[item.from.toLowerCase()];
                const rate = rates[item.to.toLowerCase()];
                
                if (rate) {
                    const rateElement = document.createElement('div');
                    rateElement.className = 'column is-6-tablet is-4-desktop';
                    rateElement.innerHTML = `
                        <div class="watch-rate-item level is-mobile">
                            <div class="level-left">
                                <div class="level-item">
                                    <div>
                                        <div class="watch-rate-pair">${item.pair}</div>
                                        <div class="watch-rate-value">${this.formatNumber(rate)}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="level-right">
                                <div class="level-item">
                                    <button class="button is-small is-danger is-outlined is-selected" onclick="app.removeFromWatchList('${item.pair}')">
                                        <span class="icon">
                                            <i class="fas fa-times"></i>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    `;
                    
                    watchListContainer.appendChild(rateElement);
                }
            } catch (error) {
                console.error('Error loading watch list rate:', error);
            }
        }
    }

    // History Methods
    loadHistory() {
        const saved = localStorage.getItem('conversionHistory');
        return saved ? JSON.parse(saved) : [];
    }

    saveHistory() {
        localStorage.setItem('conversionHistory', JSON.stringify(this.history));
    }

    addHistory(fromCurrency, toCurrency, originalAmount, convertedAmount) {
        const newEntry = {
            from: fromCurrency,
            to: toCurrency,
            originalAmount: originalAmount,
            convertedAmount: convertedAmount,
            timestamp: new Date().toISOString()
        };

        // Prevent adding duplicate history entries by checking against all existing items
        const isDuplicate = this.history.some(item =>
            item.from === newEntry.from &&
            item.to === newEntry.to &&
            item.originalAmount === newEntry.originalAmount &&
            item.convertedAmount === newEntry.convertedAmount
        );

        if (!isDuplicate) {
            this.history.unshift(newEntry); // Add to the beginning
            // Keep history limited to, e.g., 10 entries
            if (this.history.length > 10) {
                this.history.pop();
            }
            this.saveHistory();
            this.renderHistory();
        }
    }

    clearHistory() {
        if (confirm(t('confirm-clear-history'))) {
            this.history = [];
            this.saveHistory();
            this.renderHistory();
        }
    }

    renderHistory() {
        const historyContainer = document.getElementById('conversionHistory');
        const emptyMessage = document.getElementById('historyEmpty');

        if (this.history.length === 0) {
            emptyMessage.classList.remove('is-hidden');
            historyContainer.classList.add('is-hidden');
            return;
        }

        emptyMessage.classList.add('is-hidden');
        historyContainer.classList.remove('is-hidden');
        historyContainer.innerHTML = '';

        this.history.forEach(item => {
            const historyElement = document.createElement('div');
            historyElement.className = 'history-item box';
            historyElement.innerHTML = `
                <article class="media">
                    <div class="media-content">
                        <div class="content">
                            <p>
                                <span class="history-original-amount">${this.formatNumber(item.originalAmount)}</span> <span class="history-currency-from">${item.from.toUpperCase()}</span> = <span class="history-converted-amount">${this.formatNumber(item.convertedAmount)}</span> <span class="history-currency-to">${item.to.toUpperCase()}</span>
                                <br>
                                <small>${new Date(item.timestamp).toLocaleString(currentLanguage === 'zh' ? 'zh-CN' : 'en-US')}</small>
                            </p>
                        </div>
                    </div>
                    <div class="media-right">
                        <button class="delete-history-item button is-small is-danger is-outlined" data-pair="${item.from}/${item.to}" data-timestamp="${item.timestamp}">
                            <span class="icon">
                                <i class="fas fa-times"></i>
                            </span>
                        </button>
                    </div>
                </article>
            `;
            historyContainer.appendChild(historyElement);
        });

        // Attach event listeners for delete buttons
        historyContainer.querySelectorAll('.delete-history-item').forEach(button => {
            button.addEventListener('click', (event) => {
                const timestamp = event.currentTarget.dataset.timestamp;
                this.removeFromHistory(timestamp);
            });
        });

        // Attach event listeners for loading history item to form
        historyContainer.querySelectorAll('.history-item.box').forEach(itemElement => {
            itemElement.addEventListener('click', (event) => {
                // Prevent clicking on the delete button from triggering load
                if (event.target.closest('.delete-history-item')) {
                    return;
                }
                const timestamp = itemElement.querySelector('.delete-history-item').dataset.timestamp;
                const historyItem = this.history.find(h => h.timestamp === timestamp);
                if (historyItem) {
                    this.loadHistoryItemToForm(historyItem);
                }
            });
        });
    }

    removeFromHistory(timestamp) {
        this.history = this.history.filter(item => item.timestamp !== timestamp);
        this.saveHistory();
        this.renderHistory();
    }

    loadHistoryItemToForm(item) {
        document.getElementById('fromCurrency').value = item.from.toLowerCase();
        document.getElementById('toCurrency').value = item.to.toLowerCase();
        document.getElementById('amount').value = item.originalAmount;
        this.convertCurrency();
    }
}

// Initialize the application when DOM is loaded
let app;
document.addEventListener('DOMContentLoaded', () => {
    app = new CurrencyConverter();
    // Make app globally accessible for onclick handlers
    window.app = app;
});

